/**
 * Invitation Accept Component
 * Handles invitation validation and acceptance for multi-clinic system
 */

import React, { useState, useEffect } from 'react';
import { getCurrentUser, signInWithRedirect, signUp, signIn, signOut } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';

// Password Strength Indicator Component
const PasswordStrengthIndicator = ({ password }) => {
  const getStrength = () => {
    if (!password) return { score: 0, label: '', color: '#e0e0e0' };

    let score = 0;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 2) return { score, label: 'Weak', color: '#dc3545' };
    if (score === 3) return { score, label: 'Fair', color: '#ffc107' };
    if (score === 4) return { score, label: 'Good', color: '#28a745' };
    return { score, label: 'Strong', color: '#0d6efd' };
  };

  const strength = getStrength();

  if (!password) return null;

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{
        height: '4px',
        backgroundColor: '#e0e0e0',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${(strength.score / 5) * 100}%`,
          backgroundColor: strength.color,
          transition: 'all 0.3s ease'
        }} />
      </div>
      <p style={{
        fontSize: '12px',
        color: strength.color,
        margin: '4px 0 0 0',
        fontWeight: '500'
      }}>
        {strength.label}
      </p>
    </div>
  );
};

const InvitationAccept = ({ validationDelay = 1000 } = {}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [criticalError, setCriticalError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [inviteValid, setInviteValid] = useState(false);
  const [user, setUser] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [signupMethod, setSignupMethod] = useState(null); // null, 'google', 'email'
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Get token from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const inviteId = urlParams.get('inviteId');

  // Check user authentication status
  useEffect(() => {
    const checkUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        setUser(null);
      }
    };

    checkUser();

    // Listen for auth changes
    const unsubscribe = Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signedIn':
          setUser(data);
          break;
        case 'signedOut':
          setUser(null);
          break;
        default:
          break;
      }
    });

    return () => unsubscribe();
  }, []);

  // Validate invitation token (only when NOT signed in). When signed in, skip validation and wait for user action.
  useEffect(() => {
    const run = async () => {
      if (!token) {
        setCriticalError('No invitation token provided');
        setLoading(false);
        return;
      }

      if (user) {
        // Skip validation when authenticated; show UI and wait for explicit action
        setLoading(false);
        return;
      }

      try {
        // Small delay for UX parity
        await new Promise(resolve => setTimeout(resolve, validationDelay));

        const { inviteAPI } = await import('../lib/inviteAPI');
        const result = await inviteAPI.getInviteStatus(token);

        if (result.valid === true) {
          setInviteValid(true);
        } else {
          setCriticalError(result.error || 'Invalid or expired invitation');
        }
      } catch (error) {
        console.error('Error validating invitation:', error);
        setCriticalError('Failed to validate invitation. Please check the link and try again.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [token, validationDelay, user]);

  // Topics intentionally not displayed on the accept screen

  // (Auto-accept disabled by requirement; wait for user to choose method)

  // Handle Google signup
  const handleGoogleSignup = async () => {
    setError(null);
    setAccepting(true); // Disable button immediately to prevent double-clicks

    // Pass invitation token via OAuth state parameter
    // This will be returned in the callback URL after OAuth completes
    const customState = JSON.stringify({
      inviteToken: token,
      inviteId: inviteId,
      action: 'accept-invite'
    });

    // Also keep sessionStorage as backup for browsers that may have issues with state
    if (token) {
      sessionStorage.setItem('pendingInviteToken', token);
    }
    if (inviteId) {
      sessionStorage.setItem('pendingInviteId', inviteId);
    }

    await signInWithRedirect({
      provider: 'Google',
      customState: customState
    });
  };

  // Handle email/password signup
  const [signupEmail, setSignupEmail] = useState('');
  const handleEmailPasswordSignup = async (e) => {
    e.preventDefault();
    setError(null);

    if (!signupEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(signupEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 12) {
      setError('Password must be at least 12 characters long.');
      return;
    }

    setAccepting(true);

    try {
      // Sign up with email and password, passing invitation token via clientMetadata
      await signUp({
        username: signupEmail,
        password,
        options: {
          userAttributes: {
            email: signupEmail
          },
          clientMetadata: {
            invitationToken: token || '',
            inviteId: inviteId || ''
          }
        }
      });

      // Auto-login after signup
      await signIn({ username: signupEmail, password });

      // PostConfirmation Lambda will validate invitation and create user record
      setSuccess(true);

      // Redirect to main app after successful signup
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);

    } catch (error) {
      console.error('Error signing up:', error);
      if (error.name === 'UsernameExistsException') {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (error.name === 'InvalidPasswordException') {
        setError('Password does not meet requirements. Use uppercase, lowercase, numbers, and symbols.');
      } else {
        setError(error.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setAccepting(false);
    }
  };

  // Handle invitation acceptance (for existing users)
  const handleAcceptInvitation = async () => {
    if (!user) {
      // User needs to sign in first
      await signInWithRedirect();
      return;
    }
    // Block acceptance for signed-in users from the frontend side
    setError('You are already signed in. To accept this invitation, please sign out and then use the invite link again.');
  };

  const handleSignOutToContinue = async () => {
    setAccepting(true); // Disable button immediately to prevent double-clicks
    try {
      await signOut();
    } catch (_) {}
    // Reload current link (preserves inviteId/token in URL)
    window.location.reload();
  };

  // Render loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingSpinner}></div>
        <h2>Validating invitation...</h2>
        <p>Please wait while we verify your invitation.</p>
      </div>
    );
  }

  // Render critical error state (invalid invitation, etc.)
  if (criticalError) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <h2 style={styles.errorTitle}>Invitation Error</h2>
          <p style={styles.errorMessage}>{criticalError}</p>
          <div style={styles.errorActions}>
            <button
              onClick={() => window.location.href = '/'}
              style={styles.primaryButton}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render success state
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.successContainer}>
          <h2 style={styles.successTitle}>✅ Account Created Successfully!</h2>
          <p style={styles.successMessage}>
            Your account has been created and you've been granted access to your clinic(s).
          </p>
          <p>You will be redirected to the main application shortly...</p>
        </div>
      </div>
    );
  }

  // If no invitation data and not authenticated (e.g., public validation blocked/failed), show processing state
  if (!inviteValid && !user) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingSpinner}></div>
        <h2>Processing invitation...</h2>
        {error ? (
          <p style={{ color: '#c00' }}>{error}</p>
        ) : (
          <p>Please wait while we complete the process.</p>
        )}
      </div>
    );
  }

  // Render invitation details and acceptance
  return (
    <div style={styles.container}>
      <div style={styles.invitationCard}>
        <h1 style={styles.title}>You're Invited!</h1>

        {/* Invitation details intentionally not shown to protect privacy */}

        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <div style={styles.actions}>
          {!user && !signupMethod ? (
            <>
              <p style={{
                fontSize: '16px',
                color: '#666',
                marginBottom: '24px',
                fontWeight: '500'
              }}>
                Choose how you'd like to sign up:
              </p>
              <button
                onClick={handleGoogleSignup}
                disabled={accepting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  padding: '12px 20px',
                  fontSize: '16px',
                  fontWeight: '500',
                  backgroundColor: 'white',
                  border: '2px solid #4285f4',
                  borderRadius: '8px',
                  cursor: accepting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '12px',
                  opacity: accepting ? 0.6 : 1
                }}
              >
                <img
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google logo"
                  style={{ width: '20px', height: '20px', marginRight: '10px' }}
                />
                Sign up with Google
              </button>

              <button
                onClick={() => setSignupMethod('email')}
                disabled={accepting}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: accepting ? 'not-allowed' : 'pointer',
                  opacity: accepting ? 0.6 : 1,
                  marginBottom: '12px'
                }}
              >
                Sign up with Email/Password
              </button>

              <button
                onClick={() => window.location.href = '/'}
                style={styles.secondaryButton}
              >
                Cancel
              </button>
            </>
          ) : !user && signupMethod === 'email' ? (
            <>
              <form onSubmit={handleEmailPasswordSignup} style={{ width: '100%' }}>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  textAlign: 'left'
                }}>
                  <label style={{ fontSize: '14px', color: '#666', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: '#fff',
                      color: '#495057',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ position: 'relative', width: '100%', marginBottom: '8px' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      paddingRight: '40px',
                      fontSize: '16px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '12px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#666'
                    }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>

                <PasswordStrengthIndicator password={password} />

                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    marginTop: '12px',
                    marginBottom: '16px',
                    boxSizing: 'border-box'
                  }}
                />

                <p style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '20px',
                  textAlign: 'left',
                  lineHeight: '1.5'
                }}>
                  Password must be at least 12 characters with uppercase, lowercase, numbers, and symbols.
                  <br />
                  <strong>Tip:</strong> Use a password manager like 1Password or LastPass to create and store secure passwords.
                </p>

                <button
                  type="submit"
                  disabled={accepting}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    fontWeight: '500',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: accepting ? 'not-allowed' : 'pointer',
                    opacity: accepting ? 0.6 : 1,
                    marginBottom: '12px'
                  }}
                >
                  {accepting ? 'Creating Account...' : 'Create Account & Sign In'}
                </button>

                <button
                  type="button"
                  onClick={() => setSignupMethod(null)}
                  disabled={accepting}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '14px',
                    backgroundColor: 'transparent',
                    color: '#007bff',
                    border: 'none',
                    cursor: accepting ? 'not-allowed' : 'pointer'
                  }}
                >
                  Back to Sign Up Options
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Signed-in identity intentionally not displayed on invite screen */}
              <div style={{
                backgroundColor: '#fff3cd',
                color: '#856404',
                padding: '12px',
                border: '1px solid #ffeeba',
                borderRadius: '6px',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                You are currently signed in. To accept this invitation, please sign out and then open the invite link again.
              </div>
              <button
                onClick={handleSignOutToContinue}
                style={styles.primaryButton}
                disabled={accepting}
              >
                Sign out to continue
              </button>
              <button
                onClick={() => window.location.href = '/'}
                style={styles.secondaryButton}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  },
  
  invitationCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '600px',
    width: '100%',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  
  title: {
    color: '#333',
    marginBottom: '30px',
    fontSize: '28px',
    fontWeight: '300'
  },
  
  invitationDetails: {
    marginBottom: '40px'
  },
  
  clinicInfo: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '25px',
    marginTop: '20px'
  },
  
  clinicName: {
    color: '#007bff',
    fontSize: '24px',
    marginBottom: '20px',
    fontWeight: '600'
  },
  
  detailsGrid: {
    textAlign: 'left'
  },
  
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e9ecef'
  },
  
  detailLabel: {
    fontWeight: '600',
    color: '#555'
  },
  
  detailValue: {
    color: '#333'
  },
  
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    alignItems: 'center'
  },
  
  signInPrompt: {
    color: '#666',
    marginBottom: '10px'
  },
  
  signedInMessage: {
    color: '#28a745',
    marginBottom: '10px'
  },
  
  primaryButton: {
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    padding: '12px 30px',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '200px',
    ':hover': {
      backgroundColor: '#0056b3'
    },
    ':disabled': {
      backgroundColor: '#ccc',
      cursor: 'not-allowed'
    }
  },
  
  secondaryButton: {
    backgroundColor: 'transparent',
    color: '#666',
    border: '2px solid #dee2e6',
    padding: '10px 25px',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      borderColor: '#adb5bd',
      color: '#495057'
    }
  },
  
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px'
  },
  
  errorContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  
  errorTitle: {
    color: '#dc3545',
    marginBottom: '20px',
    fontSize: '24px'
  },
  
  errorMessage: {
    color: '#666',
    marginBottom: '30px',
    fontSize: '16px'
  },
  
  errorActions: {
    display: 'flex',
    justifyContent: 'center'
  },
  
  successContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  
  successTitle: {
    color: '#28a745',
    marginBottom: '20px',
    fontSize: '24px'
  },
  
  successMessage: {
    color: '#666',
    marginBottom: '20px',
    fontSize: '16px'
  }
};

// Add CSS for spinner animation
const spinnerCSS = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// Inject CSS (guard for non-DOM test environment)
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = spinnerCSS;
  document.head.appendChild(styleSheet);
}

export default InvitationAccept;
